import { addDays, addMonths, addWeeks, startOfMonth, startOfWeek } from 'date-fns'

import { GranularityValue } from '../features/revenue-and-downloads/types/Filters'

/**
 * Returns a copy of `data` with explicit `{ x, y: 0 }` entries inserted for any missing
 * day / week / month bucket between consecutive data points.
 *
 * Background (GR-2349): the backend can omit days where both revenue and downloads are 0,
 * so those days never reach the chart. Chart.js then draws a straight line between the
 * surrounding non-zero data points instead of dropping to the x-axis. Even with
 * `spanGaps: false`, Chart.js only treats explicit `null` y-values as gaps — entirely
 * missing data points are still bridged. Inserting explicit zero entries makes the line
 * correctly drop to the x-axis on all-zero days.
 *
 * The 4000-iteration safety cap guards against pathological input that could otherwise
 * create an unbounded loop (e.g. timestamps in the wrong order or a malformed granularity).
 */
export const fillMissingBucketsWithZero = (data: { x: number; y: number }[] | undefined, granularity: GranularityValue): { x: number; y: number }[] => {
  if (!data || data.length < 2) return data ?? []

  const sorted = [...data].sort((a, b) => a.x - b.x)
  const bucketStart = (ts: number): number => {
    switch (granularity) {
      case GranularityValue.Week:
        return startOfWeek(new Date(ts), { weekStartsOn: 1 }).getTime()
      case GranularityValue.Month:
        return startOfMonth(new Date(ts)).getTime()
      default:
        return new Date(new Date(ts).setHours(0, 0, 0, 0)).getTime()
    }
  }
  const nextBucket = (ts: number): number => {
    switch (granularity) {
      case GranularityValue.Week:
        return addWeeks(new Date(ts), 1).getTime()
      case GranularityValue.Month:
        return addMonths(new Date(ts), 1).getTime()
      default:
        return addDays(new Date(ts), 1).getTime()
    }
  }

  const result: { x: number; y: number }[] = []
  for (let i = 0; i < sorted.length; i++) {
    result.push(sorted[i])
    if (i === sorted.length - 1) break

    const currentBucket = bucketStart(sorted[i].x)
    const nextDataBucket = bucketStart(sorted[i + 1].x)
    let cursor = nextBucket(currentBucket)
    let safety = 0
    while (cursor < nextDataBucket && safety < 4000) {
      result.push({ x: cursor, y: 0 })
      cursor = nextBucket(cursor)
      safety++
    }
  }
  return result
}
